import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Plus, X, Warning } from "@phosphor-icons/react";
import ConveyorBreakdownModal from "./ConveyorBreakdownModal";

/**
 * Supervisor-facing panel mounted inside LiveTrackingView.
 * - Lists currently active routes on this vessel
 * - Allows activating a named RBTG route (Route 1-4) or a custom conveyor list
 * - Allows deactivating an active route
 * - Log conveyor breakdown button (opens modal)
 */
export default function ConveyorRoutePanel({ vessel, canInput, division }) {
    const vesselId = vessel?.id;
    const div = division || vessel?.division || "RBTG";
    const [routes, setRoutes] = useState([]);
    const [active, setActive] = useState([]);
    const [conveyors, setConveyors] = useState([]);
    const [openBreakdown, setOpenBreakdown] = useState(false);
    const [picker, setPicker] = useState(false);
    const [pickedRouteId, setPickedRouteId] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        if (!vesselId) return;
        try {
            const [r1, r2, r3] = await Promise.all([
                api.get("/conveyor-routes", { params: { division: div } }),
                api.get(`/vessels/${vesselId}/conveyor-routes`),
                api.get("/conveyors", { params: { division: div } }),
            ]);
            setRoutes(r1.data || []);
            setActive((r2.data || []).filter((s) => s.active));
            setConveyors(r3.data || []);
        } catch (err) {
            console.warn("Failed to load conveyor routes:", err?.message || err);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vesselId, div]);

    const activate = async () => {
        if (!pickedRouteId) return;
        setLoading(true);
        try {
            await api.post(`/vessels/${vesselId}/conveyor-routes/activate`, { route_id: pickedRouteId });
            setPickedRouteId("");
            setPicker(false);
            await load();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed to activate route");
        } finally {
            setLoading(false);
        }
    };

    const deactivate = async (activeId) => {
        if (!window.confirm("End this active route?")) return;
        try {
            await api.post(`/vessels/${vesselId}/conveyor-routes/${activeId}/deactivate`);
            await load();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed to end route");
        }
    };

    const availableRoutes = routes.filter((r) => !active.find((a) => a.route_id === r.id));

    return (
        <div className="g-card shadow-sm" data-testid="conveyor-route-panel">
            <div className="g-card-header-dark">
                <Warning size={16} weight="fill" />
                <span className="font-display font-bold uppercase tracking-wide text-sm">Conveyor Route — {div}</span>
                <span className="ml-auto text-xs font-semibold tabular">{active.length} active</span>
            </div>

            <div className="p-4 space-y-3">
                {active.length === 0 ? (
                    <div className="text-center text-sm text-slate-400 py-3" data-testid="no-active-route">
                        No route active. Select the route in operation to start tracking conveyor utilisation.
                    </div>
                ) : (
                    <ul className="space-y-2" data-testid="active-routes-list">
                        {active.map((a) => (
                            <li key={a.id} className="border border-slate-200 bg-slate-50 rounded-sm p-3 flex items-start gap-3" data-testid={`active-route-${a.route_name}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="g-pill" style={{ background: "var(--grindrod-green-bg)", color: "#00A344", border: "1px solid var(--grindrod-green)" }}>
                                            LIVE
                                        </span>
                                        <span className="font-display font-bold text-sm text-[var(--grindrod-navy)]">
                                            {a.route_name}
                                        </span>
                                        {a.description && (
                                            <span className="text-xs text-slate-500">— {a.description}</span>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-1">
                                        {a.conveyors.map((c) => (
                                            <span key={c} className="px-2 py-0.5 rounded-sm border border-slate-300 bg-white tabular">{c}</span>
                                        ))}
                                    </div>
                                    <div className="text-[11px] text-slate-400 mt-1">
                                        Started by {a.started_by_name} · {a.started_at ? new Date(a.started_at).toLocaleString() : ""}
                                    </div>
                                </div>
                                {canInput && (
                                    <button
                                        onClick={() => deactivate(a.id)}
                                        className="btn-outline !py-1 !px-2 text-[11px] inline-flex items-center gap-1 !border-[var(--grindrod-red)] !text-[var(--grindrod-red)]"
                                        data-testid={`deactivate-route-${a.id}`}
                                    >
                                        <X size={11} weight="bold" /> End
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                {canInput && vessel?.status === "active" && (
                    <div className="pt-2 border-t border-slate-100 space-y-2">
                        {!picker ? (
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => setPicker(true)}
                                    className="btn-primary inline-flex items-center gap-2 text-xs"
                                    data-testid="open-route-picker-btn"
                                    disabled={availableRoutes.length === 0}
                                >
                                    <Plus size={13} weight="bold" /> Activate route
                                </button>
                                <button
                                    onClick={() => setOpenBreakdown(true)}
                                    className="btn-outline inline-flex items-center gap-2 text-xs !border-[var(--grindrod-red)] !text-[var(--grindrod-red)]"
                                    data-testid="log-conveyor-breakdown-btn"
                                >
                                    <Warning size={13} weight="bold" /> Log conveyor breakdown
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-wrap items-center gap-2" data-testid="route-picker">
                                <select
                                    value={pickedRouteId}
                                    onChange={(e) => setPickedRouteId(e.target.value)}
                                    className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm flex-1 min-w-[200px]"
                                    data-testid="route-picker-select"
                                >
                                    <option value="">— Pick a route —</option>
                                    {availableRoutes.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.name} — {r.description} ({r.conveyors.join(" → ")})
                                        </option>
                                    ))}
                                </select>
                                <button
                                    onClick={activate}
                                    disabled={!pickedRouteId || loading}
                                    className="btn-primary text-xs disabled:opacity-40"
                                    data-testid="confirm-activate-route-btn"
                                >
                                    {loading ? "Starting…" : "Start route"}
                                </button>
                                <button
                                    onClick={() => { setPicker(false); setPickedRouteId(""); }}
                                    className="btn-outline text-xs"
                                    data-testid="cancel-route-picker-btn"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {openBreakdown && (
                <ConveyorBreakdownModal
                    vessel={vessel}
                    conveyors={conveyors}
                    activeRoutes={active}
                    onClose={() => setOpenBreakdown(false)}
                    onSubmitted={load}
                />
            )}
        </div>
    );
}
